/**
 * ROBUST SAVE MANAGER - Web version (Cloud + localStorage)
 *
 * Save: localStorage (always, sync-safe) + cloud (async, throttled)
 * Load: cloud first → localStorage fallback → memory backup
 * Emergency (beforeunload): localStorage only (must be synchronous)
 *
 * Cloud failures are silent — localStorage always works as backup.
 */

import { debug } from './debug.js';
import { cloudSave, cloudLoad, cloudExists, cloudClear } from './cloudSaveManager.js';

// Keep last 3 saves in memory as final failsafe
const memoryBackups = [];

// Auth user ID — set by saveManager when auth is ready
let _userId = null;

export function setUserId(userId) {
  _userId = userId;
  debug('[RobustSave] User ID set:', userId ? userId.slice(0, 8) + '...' : 'null');
}

/**
 * Save with multiple failsafes:
 * 1. Memory backup (always)
 * 2. localStorage with rotation (always, sync-safe)
 * 3. Cloud save (async, throttled, silent failure)
 */
export async function robustSave(worldState) {
  // CRITICAL: Check isInitialized first
  if (!worldState?.isInitialized) {
    debug('[RobustSave] BLOCKED: Cannot save uninitialized state!');
    return { success: false, error: 'State not initialized' };
  }

  // CRITICAL: Never save default/empty state
  if (isDefaultState(worldState)) {
    debug('[RobustSave] BLOCKED: Refusing to save default state!');
    return { success: false, error: 'Cannot save default state' };
  }

  // 1. Keep memory backup
  memoryBackups.unshift(JSON.parse(JSON.stringify(worldState)));
  if (memoryBackups.length > 3) memoryBackups.pop();

  // 2. localStorage with rotation (always runs — sync-safe fallback)
  let localResult = { success: false };
  try {
    const primary = localStorage.getItem('airlineOpsWorldState');
    if (primary) {
      const backup = localStorage.getItem('airlineOpsWorldState_backup');
      if (backup) {
        localStorage.setItem('airlineOpsWorldState_emergency', backup);
      }
      localStorage.setItem('airlineOpsWorldState_backup', primary);
    }

    localStorage.setItem('airlineOpsWorldState', JSON.stringify(worldState));
    localResult = { success: true, path: 'localStorage' };
  } catch (error) {
    debug('[RobustSave] localStorage failed:', error);
  }

  // 3. Cloud save (async, throttled — does not block)
  if (_userId) {
    cloudSave(_userId, worldState).catch(err => {
      debug('[RobustSave] Cloud save failed (non-blocking):', err);
    });
  }

  return localResult;
}

/**
 * Load with automatic recovery:
 * 1. Try cloud (most authoritative if available)
 * 2. Try localStorage (primary → backup → emergency)
 * 3. Memory backup (last resort)
 *
 * If both cloud and local exist, use whichever has the higher dayNumber.
 */
export async function robustLoad() {
  let cloudData = null;
  let localData = null;

  // 1. Try cloud
  if (_userId) {
    try {
      const cloudResult = await cloudLoad(_userId);
      if (cloudResult.success && cloudResult.exists && cloudResult.data) {
        if (isValidSave(cloudResult.data)) {
          cloudData = repairSave(cloudResult.data);
          debug('[RobustLoad] Cloud save found (day', cloudData.dayNumber, ')');
        }
      }
    } catch (err) {
      debug('[RobustLoad] Cloud load failed:', err);
    }
  }

  // 2. Try localStorage
  const localAttempts = [
    { name: 'primary', key: 'airlineOpsWorldState' },
    { name: 'backup', key: 'airlineOpsWorldState_backup' },
    { name: 'emergency', key: 'airlineOpsWorldState_emergency' }
  ];

  for (const attempt of localAttempts) {
    try {
      const raw = localStorage.getItem(attempt.key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isValidSave(parsed)) {
          localData = repairSave(parsed);
          debug(`[RobustLoad] localStorage ${attempt.name} found (day ${localData.dayNumber})`);
          break;
        }
      }
    } catch (error) {
      debug(`[RobustLoad] localStorage ${attempt.name} failed:`, error);
    }
  }

  // 3. Pick the best save (higher dayNumber wins, cloud wins ties)
  if (cloudData && localData) {
    if (cloudData.dayNumber >= localData.dayNumber) {
      debug('[RobustLoad] Using cloud save (day', cloudData.dayNumber, '>= local day', localData.dayNumber, ')');
      return { success: true, data: cloudData, exists: true, source: 'cloud' };
    } else {
      debug('[RobustLoad] Using local save (day', localData.dayNumber, '> cloud day', cloudData.dayNumber, ')');
      return { success: true, data: localData, exists: true, source: 'localStorage' };
    }
  }

  if (cloudData) {
    debug('[RobustLoad] Using cloud save (no local)');
    return { success: true, data: cloudData, exists: true, source: 'cloud' };
  }

  if (localData) {
    debug('[RobustLoad] Using local save (no cloud)');
    return { success: true, data: localData, exists: true, source: 'localStorage' };
  }

  // 4. Memory backup (last resort)
  if (memoryBackups.length > 0) {
    debug('[RobustLoad] Using memory backup!');
    return { success: true, data: memoryBackups[0], exists: true, source: 'memory' };
  }

  debug('[RobustLoad] No valid saves found anywhere');
  return { success: false, data: null, exists: false, error: 'No valid saves found' };
}

/**
 * Check if this is default/starter state (NEVER save this)
 */
function isDefaultState(data) {
  if (!data) return true;

  const isStarterCash = data.airline?.cash === 40000000;
  const isDayOne = data.dayNumber === 1;
  const noAircraft = !data.aircraft || data.aircraft.length === 0;
  const defaultName = !data.airline?.name ||
                      data.airline.name === 'Your Airline' ||
                      data.airline.name === '';

  if (isStarterCash && isDayOne && noAircraft) {
    return true;
  }

  if (defaultName && noAircraft && isDayOne) {
    return true;
  }

  return false;
}

/**
 * Validate save data
 */
function isValidSave(data) {
  if (!data || typeof data !== 'object') return false;
  if (!data.airline || typeof data.airline.cash !== 'number') return false;
  if (typeof data.dayNumber !== 'number' || data.dayNumber < 1) return false;
  if (data.airline.cash < 0 || !isFinite(data.airline.cash)) return false;
  if (isDefaultState(data)) return false;
  return true;
}

/**
 * Repair common save issues
 */
function repairSave(data) {
  if (!data.airline) data.airline = { name: 'Recovery Airline', cash: 10000000 };
  if (!data.aircraft) data.aircraft = [];
  if (!data.flights) data.flights = [];
  if (!data.routes) data.routes = [];
  if (!data.crew) data.crew = [];

  if (!isFinite(data.airline.cash) || data.airline.cash < 0) {
    data.airline.cash = 10000000;
  }

  if (!data.dayNumber || data.dayNumber < 1) {
    data.dayNumber = 1;
  }

  delete data.isNewGame;
  return data;
}

/**
 * Clear all saves (for new game)
 */
export async function clearAllSaves() {
  memoryBackups.length = 0;

  // Clear localStorage
  localStorage.removeItem('airlineOpsWorldState');
  localStorage.removeItem('airlineOpsWorldState_backup');
  localStorage.removeItem('airlineOpsWorldState_emergency');

  // Clear cloud
  if (_userId) {
    cloudClear(_userId).catch(err => {
      debug('[RobustSave] Cloud clear failed (non-blocking):', err);
    });
  }
}

/**
 * Check if any save exists (local or cloud)
 */
export async function anySaveExists() {
  if (memoryBackups.length > 0) return true;
  if (localStorage.getItem('airlineOpsWorldState')) return true;
  if (localStorage.getItem('airlineOpsWorldState_backup')) return true;

  // Check cloud
  if (_userId) {
    try {
      return await cloudExists(_userId);
    } catch {
      return false;
    }
  }

  return false;
}

export default {
  save: robustSave,
  load: robustLoad,
  clear: clearAllSaves,
  exists: anySaveExists,
  setUserId,
};
